import React, { useState } from 'react';
import imageCompression from 'browser-image-compression';

const ImageUploader = ({
  onChange,
  imageFile,
  setImageFile,
  previewUrl,
  ingredientImageFile,
  setIngredientImageFile,
  ingredientPreviewUrl,
  setIngredientPreviewUrl,
  setPreviewUrl,
  imageFor = 'product',
  existingImageUrl = '',
  size = 200,
}) => {
  const [error, setError] = useState(null);
  const [isCompressing, setIsCompressing] = useState(false);

  const allowedFileTypes = [
    'image/png',
    'image/jpeg',
    'image/svg+xml',
    'image/gif',
    'image/vnd.microsoft.icon',
    'image/x-icon',
  ];

  const maxSizeMB = 2;

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    setError(null);

    if (!file) return;

    if (!allowedFileTypes.includes(file.type)) {
      setError('Unsupported file type. Please select a valid image.');
      return;
    }

    setIsCompressing(true);

    try {
      const options = {
        maxSizeMB: maxSizeMB,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      };

      const compressedFile = await imageCompression(file, options);

      if (compressedFile.size / 1024 / 1024 > maxSizeMB) {
        setError('File is too large even after compression. Must be under 2MB.');
        setIsCompressing(false);
        return;
      }

      const preview = URL.createObjectURL(compressedFile);

      if (imageFor === 'ingredients-image') {
        setIngredientImageFile(compressedFile);
        setIngredientPreviewUrl(preview);
      } else {
        setImageFile(compressedFile);
        setPreviewUrl(preview);
      }

      onChange && onChange(compressedFile);
    } catch (err) {
      console.error('Compression error:', err);
      setError('Something went wrong during image processing.');
    } finally {
      setIsCompressing(false);
    }
  };

  const handleRemove = () => {
    if (imageFor === 'ingredients-image') {
      setIngredientImageFile('');
      setIngredientPreviewUrl('');
    } else {
      setImageFile(null);
      setPreviewUrl(null);
    }

    setError(null);
    setIsCompressing(false);
    onChange && onChange(null);
  };

  const blobPreview = imageFor === 'ingredients-image' ? ingredientPreviewUrl : previewUrl;
  const remoteUrl = typeof existingImageUrl === 'string' ? existingImageUrl.trim() : '';
  const showImage = !!(blobPreview || remoteUrl);

  const browseLabel =
    imageFor === 'ingredients-image'
      ? 'Browse ingredients image'
      : imageFor === 'store'
        ? 'Browse image'
        : 'Browse Image';
  const helperLine =
    imageFor === 'ingredients-image'
      ? 'Upload ingredients image'
      : imageFor === 'store'
        ? 'Store logo or image (max 2MB)'
        : 'Upload Product Image';

  const browseLabelStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: imageFor === 'store' ? '14px' : '12px',
    padding: '12px 16px',
    background: imageFor === 'ingredients-image' ? 'gray' : '#007bff',
    color: '#fff',
    borderRadius: 5,
    cursor: 'pointer',
    opacity: isCompressing ? 0.6 : 1,
    width:
      imageFor === 'ingredients-image'
        ? '160px'
        : imageFor === 'store'
          ? typeof size === 'number'
            ? `min(100%, ${size}px)`
            : '100%'
          : '140px',
    minHeight: imageFor === 'ingredients-image' ? '40px' : '36px',
    pointerEvents: isCompressing ? 'none' : 'auto',
    fontSize: 13,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
  };

  return (
    <>
      {isCompressing && (
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          <div className='loader' />
          <span style={{ color: '#007bff' }}>Compressing image…</span>
        </div>
      )}

      {showImage ? (
        <>
          <div
            style={
              typeof size === 'number'
                ? { position: 'relative', display: 'inline-block' }
                : { position: 'relative', display: 'block', width: '100%' }
            }
          >
            <img
              src={blobPreview || remoteUrl}
              alt='Preview'
              style={{
                marginTop: imageFor === 'store' ? 0 : '25px',
                width: size,
                height: typeof size === 'number' ? size : undefined,
                aspectRatio: typeof size === 'number' ? undefined : '1 / 1',
                objectFit: 'cover',
                borderRadius: 8,
                border: '1px solid #ddd',
                opacity: isCompressing ? 0.4 : 1,
                transition: 'opacity 0.3s',
              }}
            />
            {!isCompressing && (
              <button
                type='button'
                onClick={handleRemove}
                style={{
                  position: 'absolute',
                  top: imageFor === 'store' ? 6 : -33,
                  right: imageFor === 'store' ? 6 : -15,
                  background: 'red',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: 24,
                  height: 24,
                  cursor: 'pointer',
                }}
                title='Remove'
              >
                ×
              </button>
            )}
          </div>
          {!blobPreview && remoteUrl && !isCompressing && (
            <label style={browseLabelStyle}>
              Upload image
              <input
                type='file'
                accept='image/*'
                onChange={handleImageChange}
                style={{ display: 'none' }}
              />
            </label>
          )}
        </>
      ) : (
        <>
          <label style={{ ...browseLabelStyle, marginTop: imageFor === 'store' ? 0 : '20px' }}>
            {browseLabel}
            <input
              type='file'
              accept='image/*'
              onChange={handleImageChange}
              style={{ display: 'none' }}
            />
          </label>

          {imageFor !== 'store' && (
            <div style={{ marginTop: '10px', color: 'gray', fontSize: 13 }}>{helperLine}</div>
          )}
        </>
      )}

      {error && <div style={{ color: 'red', marginTop: '0.5rem', fontSize: 14 }}>{error}</div>}

      {/* Spinner CSS */}
      <style>{`
        .loader {
          border: 3px solid #f3f3f3;
          border-top: 3px solid #007bff;
          border-radius: 50%;
          width: 16px;
          height: 16px;
          animation: spin 0.8s linear infinite;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default ImageUploader;
